import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

export function CaseChat({ caseId }) {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = useMutation({
    mutationFn: async (message) =>
      (await api.post(`/cases/${caseId}/chat`, { message, session_id: sessionId })).data,
    onSuccess: (data) => {
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    },
    onError: (e) => {
      toast.error(apiError(e, t));
      setMessages((m) => m.slice(0, -1));
    },
  });

  const onSend = () => {
    const msg = input.trim();
    if (!msg || send.isPending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    send.mutate(msg);
  };

  return (
    <section data-testid="case-chat" className="mt-6">
      <h3 className="mb-2 flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
        <MessageCircle size={15} /> {t("e5.chatTitle")}
      </h3>
      <div className="rounded-2xl border border-[#E2EAE5] bg-white p-3">
        <div data-testid="case-chat-messages" className="max-h-80 space-y-2 overflow-y-auto">
          {messages.length === 0 ? (
            <p className="px-1 py-6 text-center text-xs text-[#B0C2B8]" dir="auto">{t("e5.chatEmpty")}</p>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                data-testid={`case-chat-msg-${m.role}`}
                dir="auto"
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "ml-auto bg-[#2D5A4C] text-white"
                    : "mr-auto whitespace-pre-wrap border border-[#E2EAE5] bg-[#F8FAF8] text-[#1A2E26]"
                }`}
              >
                {m.content}
              </div>
            ))
          )}
          {send.isPending && (
            <div className="mr-auto flex items-center gap-2 rounded-2xl border border-[#E2EAE5] bg-[#F8FAF8] px-3 py-2 text-xs text-[#7A9488]">
              <Loader2 size={13} className="animate-spin" /> {t("e5.chatThinking")}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="mt-2 flex gap-2">
          <input
            data-testid="case-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSend(); }}
            placeholder={t("e5.chatPlaceholder")}
            dir="auto"
            className="h-10 min-w-0 flex-1 rounded-xl border border-[#E2EAE5] px-3 text-sm"
          />
          <button
            data-testid="case-chat-send"
            onClick={onSend}
            disabled={!input.trim() || send.isPending}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#2D5A4C] text-white active:scale-95 disabled:opacity-50"
          >
            <Send size={16} />
          </button>
        </div>
        <p className="mt-1.5 px-1 text-[10px] text-[#B0C2B8]">{t("e5.chatDisclaimer")}</p>
      </div>
    </section>
  );
}
